import { useState, useEffect } from 'react'
import {
  Plus,
  Edit,
  Trash2,
  Save,
  X,
  Leaf,
  Wheat,
  Milk,
  Egg,
  Fish,
  Apple,
  Utensils,
  Ban,
  CheckCircle,
  XCircle
} from 'lucide-react'
import { toast } from 'react-hot-toast'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { DetailHeader } from '@/components/ui/DetailHeader'
import DataTable from '@/components/ui/DataTable'

export interface DietaryTag {
  id: string
  label: string
  labelEn: string
  icon: string
  color: string
  description: string
  enabled: boolean
}

const DEFAULT_DIETARY_TAGS: DietaryTag[] = [
  { id: 'vegetarian', label: 'Végétarien', labelEn: 'Vegetarian', icon: 'Leaf', color: 'green', description: 'Convient aux végétariens', enabled: true },
  { id: 'vegan', label: 'Vegan', labelEn: 'Vegan', icon: 'Leaf', color: 'emerald', description: 'Convient aux vegans', enabled: true },
  { id: 'gluten-free', label: 'Sans gluten', labelEn: 'Gluten-Free', icon: 'Wheat', color: 'amber', description: 'Sans gluten', enabled: true },
  { id: 'lactose-free', label: 'Sans lactose', labelEn: 'Lactose-Free', icon: 'Milk', color: 'blue', description: 'Sans lactose', enabled: true },
  { id: 'organic', label: 'Bio', labelEn: 'Organic', icon: 'Apple', color: 'green', description: 'Certifié biologique', enabled: true },
  { id: 'halal', label: 'Halal', labelEn: 'Halal', icon: 'Utensils', color: 'teal', description: 'Certifié halal', enabled: true },
  { id: 'kosher', label: 'Casher', labelEn: 'Kosher', icon: 'Utensils', color: 'purple', description: 'Certifié casher', enabled: true },
  { id: 'nut-free', label: 'Sans noix', labelEn: 'Nut-Free', icon: 'Ban', color: 'red', description: 'Sans traces de noix', enabled: true },
]

const STORAGE_KEY = 'dietary_tags_config'

const getIconComponent = (iconName: string, className: string = 'h-5 w-5') => {
  const icons: Record<string, React.ReactNode> = {
    Leaf: <Leaf className={className} />,
    Wheat: <Wheat className={className} />,
    Milk: <Milk className={className} />,
    Egg: <Egg className={className} />,
    Fish: <Fish className={className} />,
    Apple: <Apple className={className} />,
    Utensils: <Utensils className={className} />,
    Ban: <Ban className={className} />,
  }
  return icons[iconName] || <Leaf className={className} />
}

const getColorClasses = (color: string) => {
  const colorMap: Record<string, { bg: string; text: string; border: string }> = {
    green: { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-200' },
    emerald: { bg: 'bg-emerald-100', text: 'text-emerald-800', border: 'border-emerald-200' },
    amber: { bg: 'bg-amber-100', text: 'text-amber-800', border: 'border-amber-200' },
    blue: { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-200' },
    teal: { bg: 'bg-teal-100', text: 'text-teal-800', border: 'border-teal-200' },
    purple: { bg: 'bg-purple-100', text: 'text-purple-800', border: 'border-purple-200' },
    red: { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-200' },
  }
  return colorMap[color] || { bg: 'bg-gray-100', text: 'text-gray-800', border: 'border-gray-200' }
}

const DietaryTagsPage = () => {
  const [tags, setTags] = useState<DietaryTag[]>([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editingTag, setEditingTag] = useState<DietaryTag | null>(null)
  const [formData, setFormData] = useState({
    id: '',
    label: '',
    labelEn: '',
    icon: 'Leaf',
    color: 'green',
    description: '',
    enabled: true
  })
  const [formErrors, setFormErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    loadTags()
  }, [])

  const loadTags = () => {
    setLoading(true)
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        const parsed = JSON.parse(stored)
        setTags(parsed)
      } else {
        // Initialize with defaults
        localStorage.setItem(STORAGE_KEY, JSON.stringify(DEFAULT_DIETARY_TAGS))
        setTags(DEFAULT_DIETARY_TAGS)
      }
    } catch (err) {
      console.error('Failed to load dietary tags:', err)
      setTags(DEFAULT_DIETARY_TAGS)
    } finally {
      setLoading(false)
    }
  }

  const saveTags = (updatedTags: DietaryTag[]) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedTags))
      setTags(updatedTags)
      toast.success('Étiquettes alimentaires sauvegardées')
    } catch (err) {
      console.error('Failed to save dietary tags:', err)
      toast.error('Erreur lors de la sauvegarde')
    }
  }

  const resetToDefaults = () => {
    if (window.confirm('Réinitialiser les étiquettes alimentaires aux valeurs par défaut ?')) {
      saveTags(DEFAULT_DIETARY_TAGS)
    }
  }

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {}
    
    if (!formData.id.trim()) {
      errors.id = 'L\'identifiant est requis'
    } else if (!/^[a-z0-9-]+$/.test(formData.id)) {
      errors.id = 'L\'identifiant doit contenir uniquement des lettres minuscules, chiffres et tirets'
    } else if (!editingTag && tags.some(t => t.id === formData.id)) {
      errors.id = 'Cet identifiant existe déjà'
    }
    
    if (!formData.label.trim()) {
      errors.label = 'Le libellé est requis'
    }
    
    if (!formData.labelEn.trim()) {
      errors.labelEn = 'Le libellé anglais est requis'
    }
    
    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleAdd = () => {
    setEditingTag(null)
    setFormData({
      id: '',
      label: '',
      labelEn: '',
      icon: 'Leaf',
      color: 'green',
      description: '',
      enabled: true
    })
    setFormErrors({})
    setShowForm(true)
  }

  const handleEdit = (tag: DietaryTag) => {
    setEditingTag(tag)
    setFormData({
      id: tag.id,
      label: tag.label,
      labelEn: tag.labelEn,
      icon: tag.icon,
      color: tag.color,
      description: tag.description,
      enabled: tag.enabled
    })
    setFormErrors({})
    setShowForm(true)
  }

  const handleDelete = (tagId: string) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette étiquette alimentaire ?')) {
      const updated = tags.filter(t => t.id !== tagId)
      saveTags(updated)
    }
  }

  const handleToggleEnabled = (tagId: string) => {
    const updated = tags.map(t =>
      t.id === tagId ? { ...t, enabled: !t.enabled } : t
    )
    saveTags(updated)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm()) return
    
    if (editingTag) {
      const updated = tags.map(t =>
        t.id === editingTag.id
          ? { ...formData }
          : t
      )
      saveTags(updated)
    } else {
      saveTags([...tags, formData])
    }
    
    setShowForm(false)
    setEditingTag(null)
  }

  const handleCancel = () => {
    setShowForm(false)
    setEditingTag(null)
    setFormErrors({})
  }

  const iconOptions = [
    { value: 'Leaf', label: 'Feuille (Végétarien/Vegan)' },
    { value: 'Wheat', label: 'Blé (Sans gluten)' },
    { value: 'Milk', label: 'Lait (Sans lactose)' },
    { value: 'Egg', label: 'Œuf' },
    { value: 'Fish', label: 'Poisson' },
    { value: 'Apple', label: 'Pomme (Bio)' },
    { value: 'Utensils', label: 'Couverts (Halal/Casher)' },
    { value: 'Ban', label: 'Interdit (Sans noix)' },
  ]

  const colorOptions = [
    { value: 'green', label: 'Vert' },
    { value: 'emerald', label: 'Émeraude' },
    { value: 'amber', label: 'Ambre' },
    { value: 'blue', label: 'Bleu' },
    { value: 'teal', label: 'Sarcelle' },
    { value: 'purple', label: 'Violet' },
    { value: 'red', label: 'Rouge' },
  ]

  const columns = [
    {
      key: 'icon',
      label: 'Icône',
      sortable: false,
      render: (_value: any, row: DietaryTag) => (
        <div className={`flex items-center justify-center w-10 h-10 rounded-full ${getColorClasses(row.color).bg}`}>
          {getIconComponent(row.icon, 'h-5 w-5')}
        </div>
      )
    },
    {
      key: 'id',
      label: 'Identifiant',
      sortable: true,
      render: (value: string) => (
        <span className="font-mono text-sm text-gray-600">{value}</span>
      )
    },
    {
      key: 'label',
      label: 'Libellé',
      sortable: true,
      render: (value: string, row: DietaryTag) => (
        <div>
          <div className="font-medium text-gray-900">{value}</div>
          <div className="text-sm text-gray-500">{row.labelEn}</div>
        </div>
      )
    },
    {
      key: 'description',
      label: 'Description',
      sortable: false,
      render: (value: string) => (
        <span className="text-sm text-gray-600">{value || '—'}</span>
      )
    },
    {
      key: 'color',
      label: 'Couleur',
      sortable: false,
      render: (value: string) => {
        const colors = getColorClasses(value)
        return (
          <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colors.bg} ${colors.text} ${colors.border} border`}>
            {value}
          </div>
        )
      }
    },
    {
      key: 'enabled',
      label: 'Statut',
      sortable: true,
      render: (value: boolean, row: DietaryTag) => (
        <button
          onClick={() => handleToggleEnabled(row.id)}
          className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-colors ${
            value
              ? 'bg-green-100 text-green-800 hover:bg-green-200'
              : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
          }`}
        >
          {value ? (
            <><CheckCircle className="h-3.5 w-3.5" /> Activé</>
          ) : (
            <><XCircle className="h-3.5 w-3.5" /> Désactivé</>
          )}
        </button>
      )
    }
  ]

  const actions = [
    {
      label: 'Modifier',
      icon: <Edit className="h-4 w-4" />,
      onClick: (row: DietaryTag) => handleEdit(row),
      variant: 'outline' as const
    },
    {
      label: 'Supprimer',
      icon: <Trash2 className="h-4 w-4" />,
      onClick: (row: DietaryTag) => handleDelete(row.id),
      variant: 'destructive' as const
    }
  ]

  return (
    <div className="space-y-6">
      <DetailHeader
        title="Étiquettes alimentaires"
        subtitle="Gérez les étiquettes alimentaires disponibles pour les produits"
        breadcrumbs={[
          { label: 'Tableau de bord', href: '/' },
          { label: 'Configuration' },
          { label: 'Étiquettes alimentaires' }
        ]}
        actions={[
          {
            label: 'Ajouter une étiquette',
            icon: <Plus className="h-5 w-5" />,
            onClick: handleAdd,
            variant: 'default' as const
          },
          {
            label: 'Réinitialiser',
            icon: <XCircle className="h-5 w-5" />,
            onClick: resetToDefaults,
            variant: 'outline' as const
          }
        ]}
      />

      {showForm && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {editingTag ? 'Modifier l\'étiquette' : 'Nouvelle étiquette alimentaire'}
            </CardTitle>
            <CardDescription>
              {editingTag
                ? 'Modifiez les informations de l\'étiquette alimentaire'
                : 'Ajoutez une nouvelle étiquette alimentaire pour les produits'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* ID */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Identifiant <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.id}
                    onChange={(e) => setFormData(prev => ({ ...prev, id: e.target.value }))}
                    placeholder="ex: gluten-free"
                    disabled={!!editingTag}
                    className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                      formErrors.id ? 'border-red-300 bg-red-50' : 'border-gray-300'
                    } ${editingTag ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  />
                  {formErrors.id && (
                    <p className="mt-1 text-xs text-red-600">{formErrors.id}</p>
                  )}
                </div>

                {/* Label (French) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Libellé (FR) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.label}
                    onChange={(e) => setFormData(prev => ({ ...prev, label: e.target.value }))}
                    placeholder="ex: Sans gluten"
                    className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                      formErrors.label ? 'border-red-300 bg-red-50' : 'border-gray-300'
                    }`}
                  />
                  {formErrors.label && (
                    <p className="mt-1 text-xs text-red-600">{formErrors.label}</p>
                  )}
                </div>

                {/* Label (English) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Libellé (EN) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.labelEn}
                    onChange={(e) => setFormData(prev => ({ ...prev, labelEn: e.target.value }))}
                    placeholder="ex: Gluten-Free"
                    className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                      formErrors.labelEn ? 'border-red-300 bg-red-50' : 'border-gray-300'
                    }`}
                  />
                  {formErrors.labelEn && (
                    <p className="mt-1 text-xs text-red-600">{formErrors.labelEn}</p>
                  )}
                </div>

                {/* Icon */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Icône
                  </label>
                  <select
                    value={formData.icon}
                    onChange={(e) => setFormData(prev => ({ ...prev, icon: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    {iconOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>

                {/* Color */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Couleur
                  </label>
                  <select
                    value={formData.color}
                    onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    {colorOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  <div className="mt-2 flex items-center gap-2">
                    <span className="text-xs text-gray-500">Aperçu:</span>
                    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${getColorClasses(formData.color).bg} ${getColorClasses(formData.color).text}`}>
                      {getIconComponent(formData.icon, 'h-3.5 w-3.5')}
                      {formData.label || 'Libellé'}
                    </span>
                  </div>
                </div>

                {/* Enabled */}
                <div className="flex items-center gap-3 pt-6">
                  <input
                    type="checkbox"
                    id="enabled"
                    checked={formData.enabled}
                    onChange={(e) => setFormData(prev => ({ ...prev, enabled: e.target.checked }))}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label htmlFor="enabled" className="text-sm text-gray-700">
                    Étiquette activée
                  </label>
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Description de l'étiquette alimentaire..."
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>

              {/* Actions */}
              <div className="flex items-center gap-3 pt-2">
                <Button type="submit" className="flex items-center gap-2">
                  <Save className="h-4 w-4" />
                  {editingTag ? 'Mettre à jour' : 'Ajouter'}
                </Button>
                <Button type="button" variant="outline" onClick={handleCancel} className="flex items-center gap-2">
                  <X className="h-4 w-4" />
                  Annuler
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <Alert className="bg-blue-50 border-blue-200">
        <AlertDescription className="text-blue-800 text-sm">
          Ces étiquettes sont utilisées pour classifier les produits alimentaires. 
          Elles apparaissent comme des filtres sur la page des produits et comme des badges sur les fiches produits.
          Les modifications sont sauvegardées localement et seront appliquées lors du prochain chargement de la page.
        </AlertDescription>
      </Alert>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold">
            Liste des étiquettes ({tags.length})
          </CardTitle>
          <CardDescription>
            Gérez les étiquettes alimentaires disponibles pour les produits de la plateforme
          </CardDescription>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={columns}
            data={tags}
            actions={actions}
            loading={loading}
            emptyMessage="Aucune étiquette alimentaire configurée"
          />
        </CardContent>
      </Card>
    </div>
  )
}

export default DietaryTagsPage
